import { Link } from "@tanstack/react-router";
import { ChevronRight, Copy } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getLocale } from "@/paraglide/runtime";
import { fromBase, toBase } from "./units";

/* ---------------------------------------------------------------------------
 * Shared UI for the science/lab calculators. Every calculator reuses these so
 * the whole family shares one on-brand look. Visuals come from the `.sci-*`
 * classes in styles.css, which remap the site design tokens (same system the
 * percentage calculator uses) — no per-tool CSS.
 * ------------------------------------------------------------------------- */

export type UnitKind = "conc" | "volume" | "mass" | "plain";

export interface SciUnit {
  value: string;
  label: string;
}

export interface SciField {
  /** DOM id, also the key in the solve() base-values record (e.g. "c1"). */
  id: string;
  label: () => string;
  kind: UnitKind;
  /** Unit options; omit for `plain` fields (e.g. molecular weight in g/mol). */
  units?: SciUnit[];
  placeholder?: string;
}

export interface SolveConfig {
  fields: SciField[];
  /**
   * Pure solve. Receives each field's value in BASE units (mol/L, L, g) with
   * the blank field as `null`; returns the solved field id + base value, or
   * `null` when the inputs don't yield a result.
   */
  solve: (
    base: Record<string, number | null>,
  ) => { id: string; value: number } | null;
  resultTestId: string;
  hintLeaveBlank: () => string;
  hintInvalid: () => string;
  resultPrefix?: () => string;
}

function convertToBase(value: number, unit: string, kind: UnitKind): number {
  return kind === "plain" ? value : toBase(value, unit);
}
function convertFromBase(value: number, unit: string, kind: UnitKind): number {
  return kind === "plain" ? value : fromBase(value, unit);
}

function formatNum(n: number, locale: string): string {
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (n !== 0 && (abs < 1e-4 || abs >= 1e7)) {
    return n.toExponential(4);
  }
  return n.toLocaleString(locale, { maximumFractionDigits: 6 });
}

/** Unit-aware "leave one field blank to solve it" calculator widget. */
export function SolveForCalc({ config }: { config: SolveConfig }) {
  const locale = getLocale();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(config.fields.map((f) => [f.id, ""])),
  );
  const [units, setUnits] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      config.fields.map((f) => [f.id, f.units?.[0]?.value ?? ""]),
    ),
  );

  const outcome = useMemo<
    | { kind: "blank" }
    | { kind: "invalid" }
    | { kind: "ok"; field: SciField; text: string }
  >(() => {
    const blanks = config.fields.filter((f) => values[f.id].trim() === "");
    if (blanks.length !== 1) return { kind: "blank" };

    const base: Record<string, number | null> = {};
    for (const f of config.fields) {
      const raw = values[f.id].trim();
      if (raw === "") {
        base[f.id] = null;
        continue;
      }
      const num = Number(raw);
      if (!Number.isFinite(num)) return { kind: "invalid" };
      base[f.id] = convertToBase(num, units[f.id], f.kind);
    }

    const solved = config.solve(base);
    if (!solved) return { kind: "invalid" };
    const field = config.fields.find((f) => f.id === solved.id);
    if (!field) return { kind: "invalid" };
    const shown = convertFromBase(solved.value, units[field.id], field.kind);
    const unitLabel =
      field.units?.find((u) => u.value === units[field.id])?.label ?? "";
    return {
      kind: "ok",
      field,
      text: `${formatNum(shown, locale)}${unitLabel ? ` ${unitLabel}` : ""}`,
    };
  }, [values, units, config, locale]);

  return (
    <div className="sci-card rounded-2xl p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        {config.fields.map((f) => {
          const isSolved = outcome.kind === "ok" && outcome.field.id === f.id;
          return (
            <div key={f.id} className="space-y-1.5">
              <Label htmlFor={f.id} className="text-muted-foreground">
                {f.label()}
              </Label>
              <div className="flex gap-2">
                <Input
                  id={f.id}
                  type="number"
                  inputMode="decimal"
                  placeholder={isSolved ? "" : (f.placeholder ?? "")}
                  value={values[f.id]}
                  onChange={(e) =>
                    setValues((s) => ({ ...s, [f.id]: e.target.value }))
                  }
                  className="border-black/15 bg-white text-neutral-900 placeholder:text-neutral-500 hover:border-black/25 dark:border-black/15 dark:bg-white dark:text-neutral-900 dark:placeholder:text-neutral-500"
                />
                {f.units && f.units.length > 0 && (
                  <select
                    aria-label={`${f.label()} unit`}
                    value={units[f.id]}
                    onChange={(e) =>
                      setUnits((s) => ({ ...s, [f.id]: e.target.value }))
                    }
                    className="sci-select shrink-0 rounded-md border px-2 text-sm"
                  >
                    {f.units.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6" aria-live="polite">
        {outcome.kind === "ok" ? (
          <div
            data-testid={config.resultTestId}
            className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4"
          >
            <span className="text-sm font-medium text-muted-foreground">
              {config.resultPrefix?.() ?? ""} {outcome.field.label()}
            </span>
            <span className="sci-result font-display text-3xl font-bold sm:text-4xl">
              {outcome.text}
            </span>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {outcome.kind === "invalid"
              ? config.hintInvalid()
              : config.hintLeaveBlank()}
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- page chrome ------------------------------ */

export function SciBreadcrumb({
  homeLabel,
  toolName,
  toolPath,
  current,
}: {
  homeLabel: string;
  toolName: string;
  toolPath: string;
  current?: string;
}) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground"
    >
      <Link to="/" className="transition-colors hover:text-foreground">
        {homeLabel}
      </Link>
      <ChevronRight className="h-3.5 w-3.5" aria-hidden />
      {current ? (
        <>
          <a
            href={toolPath}
            className="transition-colors hover:text-foreground"
          >
            {toolName}
          </a>
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          <span className="text-foreground" aria-current="page">
            {current}
          </span>
        </>
      ) : (
        <span className="text-foreground" aria-current="page">
          {toolName}
        </span>
      )}
    </nav>
  );
}

export interface SciExplainerProps {
  howToTitle: string;
  howTo: string;
  examplesTitle: string;
  examples: string[];
  faqTitle: string;
  faqs: Array<{ q: string; a: string }>;
}

/** How-to + worked examples + FAQ. This is the AdSense/SEO body content. */
export function SciExplainer(props: SciExplainerProps) {
  return (
    <div className="mt-12 space-y-10">
      <section className="sci-card space-y-4 rounded-2xl p-6 sm:p-7">
        <h2 className="font-display text-xl font-bold tracking-tight">
          {props.howToTitle}
        </h2>
        {props.howTo.split("\n\n").map((p) => (
          <p
            key={p}
            className="max-w-2xl leading-relaxed text-muted-foreground"
          >
            {p}
          </p>
        ))}
        {props.examples.length > 0 && (
          <div className="space-y-2.5 pt-1">
            <h3 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              {props.examplesTitle}
            </h3>
            <ul className="space-y-2.5">
              {props.examples.map((ex) => (
                <li
                  key={ex}
                  className="sci-example flex items-start gap-3 rounded-lg py-3 pr-4 pl-4 font-mono text-sm text-foreground/90"
                >
                  <span aria-hidden className="sci-result mt-px">
                    →
                  </span>
                  <span>{ex}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="px-1 font-display text-xl font-bold tracking-tight">
          {props.faqTitle}
        </h2>
        <dl className="space-y-3">
          {props.faqs.map((f) => (
            <div key={f.q} className="sci-panel rounded-xl p-5">
              <dt className="flex items-start gap-3 font-semibold text-foreground">
                <span
                  aria-hidden
                  className="sci-chip grid h-6 w-6 shrink-0 place-items-center rounded-md font-display text-xs font-bold"
                >
                  Q
                </span>
                <span className="pt-0.5">{f.q}</span>
              </dt>
              <dd className="mt-2 pl-9 text-sm leading-relaxed text-muted-foreground">
                {f.a}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}

/** Page shell: canvas + glow + centered column. */
export function SciPage({ children }: { children: ReactNode }) {
  return (
    <div className="sci-page relative min-h-screen">
      <div className="sci-glow pointer-events-none absolute inset-x-0 top-0 h-96" />
      <div className="relative mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        {children}
      </div>
    </div>
  );
}

/** Grid of small stat tiles (label + value). */
export function SciStatGrid({
  items,
}: {
  items: { label: string; value: string }[];
}) {
  return (
    <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {items.map((it) => (
        <div key={it.label} className="sci-panel rounded-xl px-4 py-3">
          <dt className="text-xs tracking-wide text-muted-foreground uppercase">
            {it.label}
          </dt>
          <dd className="mt-1 font-display text-xl font-bold text-foreground">
            {it.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Standard tool header (h1 + intro). */
export function SciHeader({ title, intro }: { title: string; intro: string }) {
  return (
    <header className="mt-6 mb-8">
      <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
        {title}
      </h1>
      <p className="mt-3 leading-relaxed text-muted-foreground">{intro}</p>
    </header>
  );
}

/** Labelled sequence textarea (monospace, white field both themes). */
export function SciTextArea({
  id,
  label,
  value,
  placeholder,
  onChange,
  rows = 5,
}: {
  id: string;
  label: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-muted-foreground">
        {label}
      </Label>
      <Textarea
        id={id}
        value={value}
        placeholder={placeholder}
        rows={rows}
        spellCheck={false}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        className="resize-y border-black/15 bg-white font-mono text-sm break-all text-neutral-900 placeholder:text-neutral-500 hover:border-black/25 dark:border-black/15 dark:bg-white dark:text-neutral-900 dark:placeholder:text-neutral-500"
      />
    </div>
  );
}

/** Monospace output block with a copy button. */
export function SciOutputBox({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-5">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
          {label}
        </span>
        <button
          type="button"
          aria-label="Copy result"
          onClick={() => {
            navigator.clipboard?.writeText(value).then(
              () => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
              },
              () => {},
            );
          }}
          className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <Copy className="h-3.5 w-3.5" aria-hidden />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <output
        data-testid={testId}
        className="sci-panel block max-h-64 overflow-auto rounded-xl p-4 font-mono text-sm break-all whitespace-pre-wrap text-foreground"
      >
        {value}
      </output>
    </div>
  );
}
